import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import VisitTracker from "./components/VisitTracker";

// Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import NetworkPage from "./pages/NetworkPage";
import ProgramsPage from "./pages/ProgramsPage";
import PublicationsPage from "./pages/PublicationsPage";
import PartnersPage from "./pages/PartnersPage";
import ContactPage from "./pages/ContactPage";
import MembershipPage from "./pages/MembershipPage";
import StudentApplicationPage from "./pages/StudentApplicationPage";
import AdminPage from "./pages/AdminPage";
import MediaKitPage from "./pages/MediaKitPage";
import DonatePage from "./pages/DonatePage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";

// Secure Admin Route Component
const SecureAdminRoute = () => {
  const location = useLocation();
  const path = location.pathname;
  
  // Check if path matches /admin followed by 6 digits (DDMMYY)
  const adminMatch = path.match(/^\/admin(\d{6})$/);
  
  if (!adminMatch) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-6xl font-bold text-blue-900 mb-4">404</h1>
          <p className="text-xl text-gray-600 mb-8">Page non trouvée</p>
          <a href="/" className="text-blue-600 hover:underline">
            Retour à l'accueil
          </a>
        </div>
      </div>
    );
  }
  
  const dateCode = adminMatch[1];
  
  // Generate today's date code in format DDMMYY
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = String(today.getFullYear()).slice(-2);
  const todayCode = `${day}${month}${year}`;
  
  // Check if the date code matches today's date
  if (dateCode !== todayCode) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-6xl font-bold text-blue-900 mb-4">404</h1>
          <p className="text-xl text-gray-600 mb-8">Page non trouvée</p>
          <a href="/" className="text-blue-600 hover:underline">
            Retour à l'accueil
          </a>
        </div>
      </div>
    );
  }
  
  // If date matches, render the admin page
  return <AdminPage />;
};

// Catch-all component for admin routes
const AdminRouteHandler = () => {
  const location = useLocation();
  const path = location.pathname;
  
  // If path starts with /admin, use SecureAdminRoute
  if (path.startsWith('/admin')) {
    return <SecureAdminRoute />;
  }
  
  return null;
};

function AppRoutes() {
  const location = useLocation();
  
  // Check if this is an admin route
  if (location.pathname.startsWith('/admin')) {
    return <SecureAdminRoute />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/network" element={<NetworkPage />} />
      <Route path="/programs" element={<ProgramsPage />} />
      <Route path="/publications" element={<PublicationsPage />} />
      <Route path="/partners" element={<PartnersPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/membership" element={<MembershipPage />} />
      <Route path="/apply" element={<StudentApplicationPage />} />
      <Route path="/media-kit" element={<MediaKitPage />} />
      <Route path="/donate" element={<DonatePage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <VisitTracker />
          <AppRoutes />
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
